import { motion } from "framer-motion";

import { cn, scoreColor, scoreToGrade } from "@/lib/formatters";

interface AuditScorePanelProps {
  score: number | null;
  audit: Record<string, unknown> | null;
}

interface Check {
  name: string;
  score: number | null;
  flags?: string[];
}

function normalizeChecks(audit: Record<string, unknown> | null): Check[] {
  if (!audit) return [];
  const checkKeys = [
    "ai_crawler_access",
    "structured_data",
    "fact_density",
    "internal_linking",
    "heading_structure",
  ] as const;

  return checkKeys.map((key) => {
    const raw = audit[key] as { score?: number; flags?: string[] } | undefined;
    return {
      name: key.replace(/_/g, " "),
      score: raw?.score ?? null,
      flags: raw?.flags ?? [],
    };
  });
}

export function AuditScorePanel({ score, audit }: AuditScorePanelProps) {
  const checks = normalizeChecks(audit);
  const grade = scoreToGrade(score);
  const color = scoreColor(score);

  return (
    <aside className="surface p-5 h-fit lg:sticky lg:top-4">
      <div className="text-center pb-5 border-b border-border">
        <p className="path-chip mb-2">geo score</p>
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className={cn("text-6xl font-mono", color)}
        >
          {grade}
        </motion.div>
        <p className="text-sm text-muted font-mono mt-2">
          {score != null ? `${Math.round(score)}/100` : "no data"}
        </p>
      </div>

      <div className="space-y-3 pt-4">
        {checks.map((c) => {
          const pct = c.score ?? 0;
          return (
            <div key={c.name}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted font-mono uppercase tracking-wider">{c.name}</span>
                <span className={cn("font-mono", scoreColor(c.score))}>
                  {c.score != null ? `${Math.round(c.score)}` : "—"}
                </span>
              </div>
              <div className="h-1 bg-bg rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className={cn(
                    "h-full rounded-full",
                    pct >= 80 ? "bg-success" : pct >= 60 ? "bg-warn" : "bg-danger",
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
